import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Plug, Settings, Users, Zap, type LucideIcon } from 'lucide-react';
import type { FeatureId } from '@sequesto-os/shared';
import clsx from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import { useAccess } from '../../hooks/useAccess';
import { useBreadcrumbTab } from '../../context/BreadcrumbContext';

/** The one account menu. System pages live here instead of in the sidebar. */
const ACCOUNT_LINKS: { name: string; href: string; feature: FeatureId; icon: LucideIcon }[] = [
  { name: 'Settings', href: '/settings', feature: 'settings', icon: Settings },
  { name: 'Users', href: '/users', feature: 'users', icon: Users },
  { name: 'Automations', href: '/automations', feature: 'automations', icon: Zap },
  { name: 'Integrations', href: '/integrations', feature: 'integrations', icon: Plug },
];

export function Header() {
  const { user, logout } = useAuth();
  const { canAccessArea } = useAccess();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTab = useBreadcrumbTab();
  const section = getPageTitle(pathname);

  const links = ACCOUNT_LINKS.filter((l) => canAccessArea(l.feature));
  const onAccountPage = ACCOUNT_LINKS.some((l) => pathname.startsWith(l.href));

  return (
    <header className="flex h-14 flex-none items-center justify-between border-b border-border-light bg-surface-secondary px-6">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-body-sm">
        <span className={activeTab ? 'text-text-tertiary' : 'font-medium text-text-primary'}>{section}</span>
        {activeTab && activeTab !== section && (
          <>
            <span className="text-text-muted">›</span>
            <span className="truncate font-medium text-text-primary">{activeTab}</span>
          </>
        )}
      </nav>

      <Menu as="div" className="relative">
        <MenuButton
          aria-label="Account menu"
          className={clsx(
            'flex items-center gap-2 rounded-full border px-1.5 py-1 pr-2.5 transition-colors hover:bg-surface-secondary',
            onAccountPage ? 'border-border bg-surface-secondary' : 'border-border-light'
          )}
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-inverse text-body-xs font-medium text-text-inverse">
            {user?.name?.charAt(0).toUpperCase() ?? 'U'}
          </span>
          <span className="text-body-sm font-medium text-text-primary">{user?.name}</span>
          <ChevronDown className="h-4 w-4 text-text-tertiary" aria-hidden />
        </MenuButton>

        <MenuItems
          anchor="bottom end"
          className="z-50 w-56 rounded-lg border border-border-light bg-surface-primary py-1 shadow-medium [--anchor-gap:8px] focus:outline-none"
        >
          <div className="border-b border-border-light px-3 py-2">
            <p className="text-body-sm font-medium text-text-primary">{user?.name}</p>
            <p className="truncate text-body-xs text-text-tertiary">{user?.email}</p>
          </div>
          {links.length > 0 && (
            <div className="border-b border-border-light py-1">
              {links.map(({ name, href, icon: Icon }) => (
                <MenuItem key={href}>
                  <button
                    type="button"
                    onClick={() => navigate(href)}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-body-sm data-[focus]:bg-surface-secondary data-[focus]:text-text-primary',
                      pathname.startsWith(href) ? 'font-medium text-text-primary' : 'text-text-secondary'
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {name}
                  </button>
                </MenuItem>
              ))}
            </div>
          )}
          <div className="py-1">
            <MenuItem>
              <button
                type="button"
                onClick={logout}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary data-[focus]:text-text-primary"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Sign out
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </Menu>
    </header>
  );
}

const SECTION_TITLES: [prefix: string, title: string][] = [
  ['/sales', 'Sales'],
  ['/csm', 'CSM'],
  ['/gtm', 'GTM'],
  ['/marketing', 'Marketing'],
  ['/finance', 'Finance'],
  ['/resources', 'Resources'],
  ['/documents', 'Brand docs'],
  ['/organizer', 'Organizer'],
  ['/integrations', 'Integrations'],
  ['/automations', 'Automations'],
  ['/james', 'James'],
  ['/users', 'Users'],
  ['/settings', 'Settings'],
  ['/signatures', 'Signatures'],
  ['/oauth', 'Integrations'],
];

function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Home';
  // A URL no section owns lands on the "Page not found" route, so it is not "Home".
  return SECTION_TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'Page not found';
}
